import { lighten } from 'polished';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { AppState } from 'src/AppState';
import styled from 'styled-components';

import { EasyClaimDocumentType } from '../easyclaim.api.client.typescript/src/index';
import { PlaceholderImage } from '../easyclaim.app.assets/index';
import { SelectImage, DetailContainer, DetailsContainer } from '../easyclaim.app.components';
import { ImageSelectionService, Image } from '../easyclaim.app.services';

import { ScreenComponent } from './ScreenComponent';

interface AdditionalDocumentsState {}

function applyAdditionalDocumentImageChange(
	currentAppState: AppState,
	image?: Image,
	componentId?: string
): Image[] {
	let additionalDocumentImages = currentAppState.additionalDocumentImages;

	const index = currentAppState.additionalDocumentImages.findIndex(
		(i) => i.componentId === componentId
	);

	if (image && index === -1) {
		additionalDocumentImages = currentAppState.additionalDocumentImages.concat(image);
	} else if (image && index !== -1) {
		additionalDocumentImages[index] = image;
	} else if (!image && index !== -1) {
		additionalDocumentImages.splice(index, 1);
		additionalDocumentImages.forEach(
			(img, idx) => (img.componentId = `additionalDocumentImage-${idx}`)
		);
	}

	return additionalDocumentImages;
}

function determineNewStateAfterAdditionalDocumentUpload(
	currentAppState: AppState,
	additionalDocumentImage: Image | undefined
): AppState {
	const isAdditionalDocumentImageLoading =
		ImageSelectionService.isImageLoading(additionalDocumentImage);
	const isAnyAdditionalDocumentImageLoading =
		!!currentAppState.additionalDocumentImages &&
		currentAppState.additionalDocumentImages.some((i) => ImageSelectionService.isImageLoading(i));

	const isImageLoading =
		!!isAdditionalDocumentImageLoading || !!isAnyAdditionalDocumentImageLoading;

	const newState = Object.assign({}, currentAppState, {
		additionalDocumentImage: additionalDocumentImage,
		isImageLoading: isImageLoading,
	});

	return newState;
}

function determineNewStateAdditionalNthDocumentUpload(
	currentAppState: AppState,
	newImage: Image | undefined,
	componentId?: string
): AppState {
	const additionalDocumentImages = applyAdditionalDocumentImageChange(
		currentAppState,
		newImage,
		componentId
	);

	const isAdditionalDocumentImageLoading = ImageSelectionService.isImageLoading(
		currentAppState.additionalDocumentImage
	);
	const isAnyAdditionalDocumentImageLoading = additionalDocumentImages.some((i) =>
		ImageSelectionService.isImageLoading(i)
	);

	const isImageLoading =
		!!isAdditionalDocumentImageLoading || !!isAnyAdditionalDocumentImageLoading;

	const newState = Object.assign({}, currentAppState, {
		additionalDocumentImages: additionalDocumentImages,
		isImageLoading: isImageLoading,
	});

	return newState;
}

export class AdditionalDocuments extends ScreenComponent<AppState, AdditionalDocumentsState> {
	constructor(props: AppState) {
		super(props);
		this.state = {};
	}

	public render() {
		return (
			<div className="main-container">
				<h2 className="additional-documents-title">
					<FormattedMessage id="additionalDocuments.headline.first"></FormattedMessage>
				</h2>
				<DetailsContainer>
					<DetailContainer>
						<h3 className="additional-documents-subtitle">
							<FormattedMessage id="additionalDocuments.headline.second"></FormattedMessage>
						</h3>
						<SelectImage
							showUserFeedbackInfo={true}
							caseId={this.props.easyClaimCase!.caseId}
							componentId="additionalDocumentImage"
							fileType={EasyClaimDocumentType.Additional}
							currentImage={this.props.additionalDocumentImage}
							title="Dodatkowe dokumenty"
							placeholder={<StyledPlaceholderAdditionalDocuments />}
							onChange={(image) => {
								const newState = determineNewStateAfterAdditionalDocumentUpload(this.props, image);

								void this.props.setAppState(newState);
							}}
							easyClaimService={this.props.easyClaimService}
							configuration={this.props.easyClaimCase!._configuration!}
							inputError={this.props.inputError}
							canOverlayCamera={this.props.easyClaimCase?._configuration?.canShowOverlayOnCamera}
						/>
						<h3 className="additiona-documents-optional-images">
							<FormattedMessage id="additionalDocuments.additionalDocumentImages" />
						</h3>
						<Paragraph>
							<FormattedMessage id="additionalDocuments.optionalDocumentImages" />
						</Paragraph>
						<AdditionalImagesContainer>
							{this.props.additionalDocumentImages.map((image, i) => (
								<SelectImage
									caseId={this.props.easyClaimCase!.caseId}
									easyClaimService={this.props.easyClaimService}
									key={i}
									componentId={image.componentId}
									fileType={EasyClaimDocumentType.Additional}
									currentImage={image}
									showEditIcon={false}
									showStatusIcon={false}
									title="Dodatkowe dokumenty"
									photoIconSize="Small"
									onChange={(newImage) => {
										const newState = determineNewStateAdditionalNthDocumentUpload(
											this.props,
											newImage,
											image.componentId
										);

										void this.props.setAppState(newState);
									}}
									configuration={this.props.easyClaimCase!._configuration!}
									inputError={this.props.inputError}
									canOverlayCamera={
										this.props.easyClaimCase?._configuration?.canShowOverlayOnCamera
									}
								/>
							))}
							<SelectImage
								caseId={this.props.easyClaimCase!.caseId}
								easyClaimService={this.props.easyClaimService}
								key={this.props.additionalDocumentImages.length}
								componentId={`additionalDocumentImage-${this.props.additionalDocumentImages.length}`}
								fileType={EasyClaimDocumentType.Additional}
								showEditIcon={false}
								showStatusIcon={false}
								title="Dodatkowe dokumenty"
								photoIconSize="Small"
								onChange={(newImage) => {
									const newState = determineNewStateAdditionalNthDocumentUpload(
										this.props,
										newImage,
										newImage?.componentId
									);

									void this.props.setAppState(newState);
								}}
								configuration={this.props.easyClaimCase!._configuration!}
								disableUpdate={true}
								inputError={this.props.inputError}
								canOverlayCamera={this.props.easyClaimCase?._configuration?.canShowOverlayOnCamera}
							/>
						</AdditionalImagesContainer>
					</DetailContainer>
				</DetailsContainer>
			</div>
		);
	}
}

const StyledPlaceholderAdditionalDocuments = styled(PlaceholderImage)`
	color: ${(p) => lighten(0.4, p.theme.colors.secondary)};
	margin: 1rem;
	width: auto;
	display: block;
`;

const Paragraph = styled.p`
	margin-bottom: 1rem;
	hyphens: auto;
	user-select: none;
	cursor: default;
`;

const AdditionalImagesContainer = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr 1fr;
	grid-gap: 0.5rem;
	margin-bottom: 1rem;
	align-items: center;
`;
